import axios from '../utils/axios';
import {
  Gate, GateUpdate, DuplicateAnalysis, MergeResult, MergeRoutesResult,
} from '../types/Gate';

export async function getGates(queryString: string = ''): Promise<Gate[]> {
  const suffix = queryString ? `?${queryString}` : '';
  return axios.get(`/gates${suffix}`).then((res) => res?.data ?? []);
}

/**
 * Full update. With `session_id` in `data` a gate shared by other sessions is
 * copied for that session first, and the gate returned is the copy.
 */
export async function updateGate(gateId: string, data: GateUpdate): Promise<Gate> {
  return axios.put(`/gates/${gateId}`, data).then((res) => res.data);
}

/** Partial update: only the keys in `data` are written. */
export async function patchGate(gateId: string, data: Partial<Gate>): Promise<Gate> {
  return axios.patch(`/gates/${gateId}`, data).then((res) => res.data);
}

export async function deleteGate(gateId: string): Promise<any> {
  return axios.delete(`/gates/${gateId}`).then((res) => res.data);
}

export async function createGate(data: Partial<Gate>): Promise<Gate> {
  return axios.post('/gates', data).then((res) => res.data);
}

export async function deleteGates(gateIds: string[]): Promise<any> {
  // Batch delete multiple gates
  return Promise.all(gateIds.map((id) => deleteGate(id)));
}

export async function getGateDuplicateAnalysis(): Promise<DuplicateAnalysis> {
  return axios.get('/gates?scope=duplicate_analysis').then((res) => res?.data ?? { duplicate_groups: [], summary: {} });
}

export async function mergeGates(gateIds: string[]): Promise<MergeResult> {
  return axios.post('/gates/merge', { gate_ids: gateIds }).then((res) => res.data);
}

export interface MergeRoutesInput {
  routeIds: string[];
  /** A name for the merged route. Blank, the server dates it ("ROUTE-MERGE: 2026-10-17"). */
  description?: string;
}

export async function mergeRoutes({ routeIds, description }: MergeRoutesInput): Promise<MergeRoutesResult> {
  const body: Record<string, unknown> = { route_ids: routeIds };
  if (description?.trim()) body.description = description.trim();
  return axios.post('/routes/merge', body).then((res) => res.data);
}
